import { useState, useEffect } from 'react';
import { 
  Link2, 
  CheckCircle, 
  XCircle, 
  Clock, 
  ExternalLink,
  Loader2,
  Building2,
  AlertCircle,
  User,
  Crown
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { vendorAdminApi } from '@/services/api';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';

const STATUS_CONFIG = {
  pending: { icon: Clock, color: 'text-amber-500', bgColor: 'bg-amber-100', label: 'Pending' },
  approved: { icon: CheckCircle, color: 'text-green-600', bgColor: 'bg-green-100', label: 'Approved' },
  rejected: { icon: XCircle, color: 'text-red-500', bgColor: 'bg-red-100', label: 'Rejected' },
};

const TIER_BADGE = {
  enterprise: { icon: Crown, color: 'text-amber-600 bg-amber-100', label: 'Enterprise' },
  verified: { icon: CheckCircle, color: 'text-blue-600 bg-blue-100', label: 'Verified' },
  claimed: { icon: Building2, color: 'text-slate-600 bg-slate-100', label: 'Claimed' },
};

export function PlatformLinkRequestsManager() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [rejectNotes, setRejectNotes] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  
  const { toast } = useToast();

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const data = await vendorAdminApi.getLinkRequests(statusFilter);
      setRequests(data);
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const handleApprove = async (requestId) => {
    setIsProcessing(true);
    try {
      await vendorAdminApi.approveLinkRequest(requestId);
      toast({ title: 'Success', description: 'Request approved and platform linked' });
      fetchRequests();
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!selectedRequest || !rejectNotes.trim()) {
      toast({ title: 'Error', description: 'Rejection reason is required', variant: 'destructive' });
      return;
    }
    
    setIsProcessing(true);
    try {
      await vendorAdminApi.rejectLinkRequest(selectedRequest.id, rejectNotes);
      toast({ title: 'Success', description: 'Request rejected' });
      setSelectedRequest(null);
      setRejectNotes('');
      fetchRequests();
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setIsProcessing(false);
    }
  };

  const pendingCount = requests.filter(r => r.status === 'pending').length;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Link2 className="w-5 h-5" />
              Platform Link Requests
              {pendingCount > 0 && statusFilter === 'pending' && (
                <Badge variant="secondary">{pendingCount} pending</Badge>
              )}
            </CardTitle>
            <CardDescription>
              Review and approve vendor platform link requests
            </CardDescription>
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[150px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
              <SelectItem value="all">All</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : requests.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Link2 className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>No {statusFilter !== 'all' ? statusFilter : ''} requests found</p>
          </div>
        ) : (
          <div className="space-y-4">
            {requests.map((request) => {
              const statusConfig = STATUS_CONFIG[request.status] || STATUS_CONFIG.pending;
              const StatusIcon = statusConfig.icon;
              const tierConfig = TIER_BADGE[request.vendor_tier?.toLowerCase()] || TIER_BADGE.claimed;
              const TierIcon = tierConfig.icon;

              return (
                <div 
                  key={request.id}
                  className="border rounded-lg p-4"
                  data-testid={`link-request-${request.id}`}
                >
                  <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap mb-2">
                        <span className="font-medium text-lg">{request.platform_name}</span>
                        <Badge variant="outline" className={cn('text-xs', statusConfig.color)}>
                          <StatusIcon className="w-3 h-3 mr-1" />
                          {statusConfig.label}
                        </Badge>
                        <Badge variant="outline" className={cn('text-xs', tierConfig.color)}>
                          <TierIcon className="w-3 h-3 mr-1" />
                          {tierConfig.label}
                        </Badge>
                      </div>
                      
                      <div className="text-sm text-muted-foreground space-y-1">
                        <p className="flex items-center gap-2">
                          <User className="w-4 h-4" />
                          {request.vendor_company_name} ({request.vendor_email})
                        </p>
                        <p>
                          Submitted: {new Date(request.created_at).toLocaleDateString()}
                        </p>
                        {request.note && (
                          <p className="mt-2 p-2 bg-muted rounded">
                            <strong>Note:</strong> {request.note}
                          </p>
                        )}
                        {request.status === 'rejected' && request.admin_notes && (
                          <p className="mt-2 p-2 bg-red-50 rounded text-red-700">
                            <strong>Rejection reason:</strong> {request.admin_notes}
                          </p>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <Button
                        variant="outline"
                        size="sm"
                        asChild
                      >
                        <a href={request.proof_url} target="_blank" rel="noopener noreferrer">
                          <ExternalLink className="w-4 h-4 mr-2" />
                          View Proof
                        </a>
                      </Button>
                      
                      {request.status === 'pending' && (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-red-600 hover:text-red-700"
                            onClick={() => {
                              setSelectedRequest(request);
                              setRejectNotes('');
                            }}
                            data-testid={`reject-btn-${request.id}`}
                          >
                            <XCircle className="w-4 h-4 mr-2" />
                            Reject
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => handleApprove(request.id)}
                            disabled={isProcessing}
                            data-testid={`approve-btn-${request.id}`}
                          >
                            {isProcessing ? (
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                              <CheckCircle className="w-4 h-4 mr-2" />
                            )}
                            Approve
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>

      {/* Reject Dialog */}
      <Dialog open={!!selectedRequest} onOpenChange={(open) => !open && setSelectedRequest(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Platform Link Request</DialogTitle>
            <DialogDescription>
              Please provide a reason for rejecting this request. This will be shown to the vendor.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="p-3 bg-muted rounded-lg">
              <p className="font-medium">{selectedRequest?.platform_name}</p>
              <p className="text-sm text-muted-foreground">
                Requested by: {selectedRequest?.vendor_company_name}
              </p>
            </div>
            
            <div className="space-y-2">
              <label className="text-sm font-medium">Rejection Reason *</label>
              <Textarea
                value={rejectNotes}
                onChange={(e) => setRejectNotes(e.target.value)}
                placeholder="Explain why this request is being rejected..."
                rows={3}
                data-testid="reject-reason-input"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setSelectedRequest(null)}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleReject}
              disabled={isProcessing || !rejectNotes.trim()}
              data-testid="confirm-reject-btn"
            >
              {isProcessing ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <XCircle className="w-4 h-4 mr-2" />
              )}
              Reject Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
